import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
    Box, Tabs, Tab, Button,
    Dialog, DialogTitle,
    DialogContent, IconButton,
    CircularProgress, Skeleton, Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {useCompanyAnnualStore, useTaxStore} from '@/store/audit';
import {auditTaxComService} from '@/services/auditService/auditTaxComService';
import {TaxMain} from '@/pages/components/tax-computation/TaxMain';
import {TaxSubP1} from '@/pages/components/tax-computation/TaxSubP1';
import {TaxSubP11a} from '@/pages/components/tax-computation/TaxSubP11a';
import {TaxSubP2} from '@/pages/components/tax-computation/TaxSubP2';
import {TaxSubP21} from '@/pages/components/tax-computation/TaxSubP21';
import {TaxSubP3} from '@/pages/components/tax-computation/TaxSubP3';
import {TaxPL} from '@/pages/components/tax-computation/TaxPL';
import {LastTaxDrawer} from '@/pages/components/tax-computation/LastTaxDrawer';
import {useLastTax} from '@/pages/components/tax-computation/useLastTax';
import type {BfPools} from '@/pages/components/tax-computation/sub/DepreciationScheduleTable';
import type {
    AuditTaxComBO,
    AuditTaxComPropertyBO,
    TaxAdditionsNonCurrentAssetsVO,
    TaxComputationResult,
    TaxExtraItem,
    TaxObligationUnderFinanceLeasesVO,
    TaxPpeDetailsVO,
    TaxWithholdingScheduleVO,
} from '@/services/auditService/interface/tax-com';
import {TaxComputationType} from '@/services/auditService/interface/tax-com';
import {
    buildTaxComPayloadKey,
    computeDepreciationWdvCfPools,
    normalizePropertyList,
    patchP11CommercialAllowances,
    patchTaxMainCommercialBuilding,
    poolsFromBalancingRowName,
    mergeHydratedExtraItems,
    patchProfitsTaxFromLessTotal,
    propertyListKey,
    recomputeLessTotalWithExtras,
    resolveP1CommercialBuildingFromSavedProperties,
    toExtraItemSaveList,
} from '@/pages/components/tax-computation/taxComPayload';
import {getP1NoteConfirmFromResult, normalizeTaxComputationType} from '@/pages/components/tax-computation/taxComP1Text';
import type {AuditPeriodSlice} from '@/utils/auditPeriodSplit';
import {useQueryClient} from '@tanstack/react-query';

interface TaxDetailsTabProps {
    annualId: number;
    /** Prefer parent companyId (FsAdjustment) so open does not depend on annual cache alone */
    companyId?: number;
    open: boolean;
    onClose: () => void;
    /** MULTI PL slices from FsAdjustment; when length >= 2, show YOA tabs */
    periods?: AuditPeriodSlice[];
    /** Initial YOA when opening (active PL slice or first) */
    initialYearOfAssessment?: string | null;
    /** When true, render content only (no outer Dialog) for parent Preview Dialog */
    embedded?: boolean;
    /**
     * CLOSED: standalone Tax Com Excel merged into Zip (/fs-preview-download/download-zip).
     * Prop kept for callers; export button is always off.
     */
    hideStandaloneExport?: boolean;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    visited?: boolean;
}

function TabPanel(props: TabPanelProps) {
    const {children, value, index, visited, ...other} = props;
    const shouldRender = visited === undefined ? true : visited;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tax-tabpanel-${index}`}
            aria-labelledby={`tax-tab-${index}`}
            {...other}
        >
            <Box sx={{display: value === index ? 'block' : 'none'}}>
                {shouldRender ? children : null}
            </Box>
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `tax-tab-${index}`,
        'aria-controls': `tax-tabpanel-${index}`,
    };
}

export function TaxDetailsTab({
    annualId,
    companyId: companyIdProp,
    open,
    onClose,
    periods,
    initialYearOfAssessment,
    embedded = false,
    hideStandaloneExport = false,
}: TaxDetailsTabProps) {
    const queryClient = useQueryClient();
    const getCompanyDetailsInfo = useCompanyAnnualStore((state) => state.getCompanyDetailsInfo);
    const getTaxComputationInfo = useCompanyAnnualStore((state) => state.getTaxComputationInfo);
    /** firstAudit === 1：当年为首次审计，不展示 Last Tax 上传/查看按钮 */
    const isFirstAudit = useCompanyAnnualStore(
        (s) => Number(s.byAnnualId[annualId]?.firstAudit ?? 0) === 1,
    );
    const initialize = useTaxStore((state) => state.initialize);
    const clear = useTaxStore((state) => state.clear);

    /**
     * 后端 `queryTaxCombined` 已经一次性返回主结果 + P1.1/P1.1a/P1.2/P1.21/P1.3，
     * 因此前端只保留单一的 refresh 入口，由 store 内部统一刷新所有子表数据。
     */
    const refresh = useTaxStore((state) => state.refresh);
    const applyTaxComputationResult = useTaxStore((state) => state.applyTaxComputationResult);
    const applyP11Data = useTaxStore((state) => state.applyP11Data);

    const getTaxMainData = useTaxStore((state) => state.getTaxComputationResult);
    const getP11Data = useTaxStore((state) => state.getP11Data);
    const getP11aData = useTaxStore((state) => state.getP11aData);
    const getP12Data = useTaxStore((state) => state.getP12Data);
    const getP121Data = useTaxStore((state) => state.getP121Data);
    const getP13Data = useTaxStore((state) => state.getP13Data);

    const yoaTabs = useMemo(() => {
        const list = (periods || [])
            .map((p) => (p.yearOfAssessment || '').trim())
            .filter(Boolean);
        const unique = Array.from(new Set(list));
        return unique.length >= 2 ? unique : [];
    }, [periods]);

    const isMultiYoa = yoaTabs.length >= 2;

    const [selectedYoa, setSelectedYoa] = useState(() => (initialYearOfAssessment || '').trim());
    const effectiveYoa = isMultiYoa
        ? (selectedYoa && yoaTabs.includes(selectedYoa) ? selectedYoa : yoaTabs[0])
        : '';
    /** MULTI: only first YOA tab may edit DEPRECIATION W.D.V. b/f */
    const isFirstYoaTab = !isMultiYoa || !effectiveYoa || yoaTabs[0] === effectiveYoa;
    const wdvBfEditable = isFirstYoaTab;
    /** Session cache: YOA → computed W.D.V. c/f (feeds next YOA's W.D.V. b/f) */
    const wdvCfByYoaRef = useRef<Record<string, BfPools>>({});

    const [loading, setLoading] = useState(true);
    const savingRef = useRef(false);
    const pendingSaveRef = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isFirstLoadRef = useRef(true);
    const doSaveRef = useRef<() => Promise<void>>();
    const suppressAutoSaveRef = useRef(false);
    const skipChildParentSyncRef = useRef(false);
    const lastSavedPropertiesRef = useRef<AuditTaxComPropertyBO[] | null>(null);
    const lastSavedPayloadRef = useRef<string>('');
    const p11PropertiesRef = useRef<AuditTaxComPropertyBO[]>([]);
    // null pools = not hydrated yet — must not serialize as 0 or saveOrUpdate will wipe DB / kill prior-YOA seed
    const p1DraftRef = useRef({
        oneReducingBf: 0,
        wdvBf: 0,
        wdvBfPools: null as BfPools | null,
        baPools: null as BfPools | null,
        salesProceedPools: null as BfPools | null,
        taxPayment: 0,
        taxLossBf: 0,
        commercialFullyUsedPriorYears: 0,
        leaseholdFullyUsedPriorYears: 0,
        p1Note: null as string | null,
        p1Confirm: null as string | null,
        commercialPriorLabel: null as string | null,
        commercialPriorValue: null as number | null,
        leaseholdPriorLabel: null as string | null,
        leaseholdPriorValue: null as number | null,
    });
    const initDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [snack, setSnack] = useState<{ open: boolean; message: string } | null>(null);
    const showSnack = useCallback((message: string) => {
        setSnack({open: true, message});
    }, []);
    const [tabValue, setTabValue] = useState(0);
    const [visitedTabKeys, setVisitedTabKeys] = useState<Set<string>>(() => new Set(['p1']));
    const [hasP11Data, setHasP11Data] = useState(false);
    const [hasP11aData, setHasP11aData] = useState(false);
    const [hasP12Data, setHasP12Data] = useState(false);
    const [hasP121Data, setHasP121Data] = useState(false);
    const [hasP13Data, setHasP13Data] = useState(false);
    const [p11aData, setP11aData] = useState<TaxWithholdingScheduleVO | null>(null);
    const [p12Data, setP12Data] = useState<TaxAdditionsNonCurrentAssetsVO | null>(null);
    const [p121Data, setP121Data] = useState<TaxObligationUnderFinanceLeasesVO | null>(null);
    const [p13Data, setP13Data] = useState<TaxPpeDetailsVO | null>(null);
    const [p11Properties, setP11Properties] = useState<AuditTaxComPropertyBO[]>([]);
    const [companyId, setCompanyId] = useState<number>(0);
    const [oneReducingBf, setOneReducingBf] = useState<number>(0);
    const [wdvBf, setWdvBf] = useState<number>(0);
    const hasHydratedP11BfRef = useRef(false);

    const {
        fileInputRef: lastTaxFileInputRef,
        uploadingLastTax,
        lastTaxDrawerOpen,
        drawerWidth: lastTaxDrawerWidth,
        hasLastTax,
        currentMeta: lastTaxMeta,
        currentPreviewUrl: lastTaxPreviewUrl,
        handleLastTaxClick,
        handleReplaceClick,
        handleFileSelected: handleLastTaxFileSelected,
        closeDrawer: closeLastTaxDrawer,
    } = useLastTax({
        open,
        annualId,
        companyId,
        yoa: effectiveYoa,
        onMessage: showSnack,
    });

    // Measured Drawer Paper width (0 when closed) — same role as BankLoanDetail.drawerWidth
    const [lastTaxMeasuredWidth, setLastTaxMeasuredWidth] = useState(0);
    // Horizontal shift so Tax Dialog clears the right-anchored LastTaxDrawer (BankLoanDetail pattern)
    const [lastTaxShiftX, setLastTaxShiftX] = useState(0);
    const taxDialogPaperRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const calcShift = () => {
            if (!lastTaxDrawerOpen || lastTaxMeasuredWidth <= 0) return 0;
            const paper = taxDialogPaperRef.current;
            if (!paper) return 0;
            const dialogWidth = paper.getBoundingClientRect().width;
            const viewportWidth = window.innerWidth;
            const viewportCenter = viewportWidth / 2;
            const margin = 16;
            // Dialog centered; right edge at viewportCenter + dialogWidth/2.
            // Need: rightEdge + shiftX <= viewportWidth - drawerWidth - margin  → shiftX <= 0 when overlapping
            const room =
                viewportWidth - lastTaxMeasuredWidth - margin - (viewportCenter + dialogWidth / 2);
            if (room >= 0) return 0;
            const needed = -room;
            const maxLeft = Math.max(0, viewportCenter - dialogWidth / 2 - margin);
            return -Math.round(Math.min(needed, maxLeft));
        };

        const apply = () => {
            setLastTaxShiftX(lastTaxDrawerOpen ? calcShift() : 0);
        };
        apply();
        const onResize = () => {
            if (lastTaxDrawerOpen) setLastTaxShiftX(calcShift());
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [lastTaxDrawerOpen, lastTaxMeasuredWidth]);

    const [wdvBfPools, setWdvBfPools] = useState<BfPools | null>(null);
    const [baPools, setBaPools] = useState<BfPools | null>(null);
    const [salesProceedPools, setSalesProceedPools] = useState<BfPools | null>(null);

    const [taxPayment, setTaxPayment] = useState<number>(0);
    const [taxLossBf, setTaxLossBf] = useState<number>(0);

    const [commercialFullyUsedPriorYears, setCommercialFullyUsedPriorYears] = useState<number>(0);
    const [leaseholdFullyUsedPriorYears, setLeaseholdFullyUsedPriorYears] = useState<number>(0);
    const [p1Note, setP1Note] = useState<string | null>(null);
    const [p1Confirm, setP1Confirm] = useState<string | null>(null);
    const [commercialPriorLabel, setCommercialPriorLabel] = useState<string | null>(null);
    const [commercialPriorValue, setCommercialPriorValue] = useState<number | null>(null);
    const [leaseholdPriorLabel, setLeaseholdPriorLabel] = useState<string | null>(null);
    const [leaseholdPriorValue, setLeaseholdPriorValue] = useState<number | null>(null);
    const [extraAddItems, setExtraAddItems] = useState<TaxExtraItem[]>([]);
    const [extraLessItems, setExtraLessItems] = useState<TaxExtraItem[]>([]);
    const extraAddItemsRef = useRef<TaxExtraItem[]>([]);
    const extraLessItemsRef = useRef<TaxExtraItem[]>([]);

    const handlePropertiesInit = useCallback((properties: AuditTaxComPropertyBO[]) => {
        // First-load empty push from TaxSubP1 mount must not dirty parent (autosave / close flush)
        if (isFirstLoadRef.current && !(properties?.length)) {
            return;
        }
        setP11Properties((prev) => {
            if (propertyListKey(prev) === propertyListKey(properties)) {
                return prev;
            }
            return properties;
        });
    }, []);

    useEffect(() => {
        p11PropertiesRef.current = p11Properties;
    }, [p11Properties]);

    useEffect(() => {
        extraAddItemsRef.current = extraAddItems;
    }, [extraAddItems]);

    useEffect(() => {
        extraLessItemsRef.current = extraLessItems;
    }, [extraLessItems]);

    useEffect(() => {
        p1DraftRef.current = {
            oneReducingBf,
            wdvBf,
            wdvBfPools,
            baPools,
            salesProceedPools,
            taxPayment,
            taxLossBf,
            commercialFullyUsedPriorYears,
            leaseholdFullyUsedPriorYears,
            p1Note,
            p1Confirm,
            commercialPriorLabel,
            commercialPriorValue,
            leaseholdPriorLabel,
            leaseholdPriorValue,
        };
    }, [
        oneReducingBf,
        wdvBf,
        wdvBfPools,
        baPools,
        salesProceedPools,
        taxPayment,
        taxLossBf,
        commercialFullyUsedPriorYears,
        leaseholdFullyUsedPriorYears,
        p1Note,
        p1Confirm,
        commercialPriorLabel,
        commercialPriorValue,
        leaseholdPriorLabel,
        leaseholdPriorValue,
    ]);

    const handleP1NoteChange = useCallback((value: string) => {
        setP1Note(value);
        p1DraftRef.current = {...p1DraftRef.current, p1Note: value};
    }, []);

    const handleP1ConfirmChange = useCallback((value: string) => {
        setP1Confirm(value);
        p1DraftRef.current = {...p1DraftRef.current, p1Confirm: value};
    }, []);

    const handleCommercialPriorOverrideChange = useCallback((label: string | null, value: number | null) => {
        setCommercialPriorLabel(label);
        setCommercialPriorValue(value);
        p1DraftRef.current = {
            ...p1DraftRef.current,
            commercialPriorLabel: label,
            commercialPriorValue: value,
        };
    }, []);

    const handleLeaseholdPriorOverrideChange = useCallback((label: string | null, value: number | null) => {
        setLeaseholdPriorLabel(label);
        setLeaseholdPriorValue(value);
        p1DraftRef.current = {
            ...p1DraftRef.current,
            leaseholdPriorLabel: label,
            leaseholdPriorValue: value,
        };
    }, []);

    const flushDebouncedSave = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        void doSaveRef.current?.();
    }, []);

    /** Snapshot current YOA depreciation W.D.V. c/f into session cache (for next YOA b/f). */
    const cacheCurrentYoaWdvCf = useCallback(() => {
        if (!isMultiYoa) return;
        const yoa = (effectiveYoa || '').trim();
        if (!yoa) return;
        const draft = p1DraftRef.current;
        if (draft.wdvBfPools == null || draft.baPools == null || draft.salesProceedPools == null) return;
        const balancingList = getP11Data()?.balancingList || [];
        const cf = computeDepreciationWdvCfPools({
            wdvBf: draft.wdvBfPools,
            ba: draft.baPools,
            salesProceed: draft.salesProceedPools,
            additions: poolsFromBalancingRowName(balancingList, 'Additions'),
            ia60: poolsFromBalancingRowName(balancingList, 'I.A. - 60%'),
        });
        wdvCfByYoaRef.current[yoa] = cf;
    }, [effectiveYoa, getP11Data, isMultiYoa]);

    const buildCurrentP1Payload = useCallback((): AuditTaxComBO => {
        const draft = p1DraftRef.current;
        const yoa = (effectiveYoa || '').trim();
        const poolsReady = draft.wdvBfPools != null && draft.baPools != null && draft.salesProceedPools != null;
        const out: AuditTaxComBO = {
            companyId: Number(companyId || 0),
            annualId: Number(annualId || 0),
            yearOfAssessment: yoa || '',
            properties: (p11PropertiesRef.current || []).map((p) => ({
                ...p,
                yearOfAssessment: yoa || '',
            })),
            taxPayment: Number(draft.taxPayment) || 0,
            taxLossBf: Number(draft.taxLossBf) || 0,
            p1Note: draft.p1Note != null ? draft.p1Note : undefined,
            p1Confirm: draft.p1Confirm != null ? draft.p1Confirm : undefined,
            commercialPriorLabel: draft.commercialPriorLabel,
            commercialPriorValue: draft.commercialPriorValue,
            leaseholdPriorLabel: draft.leaseholdPriorLabel,
            leaseholdPriorValue: draft.leaseholdPriorValue,
            extraAddItems: toExtraItemSaveList(extraAddItemsRef.current, 'ADD'),
            extraLessItems: toExtraItemSaveList(extraLessItemsRef.current, 'LESS'),
        };
        // Omit P1.1 schedule fields until hydrated — backend skips null and keeps DB / carry-forward seed
        if (poolsReady) {
            out.oneReducingBf = Number(draft.oneReducingBf) || 0;
            out.wdvBf = Number(draft.wdvBf) || 0;
            out.wdvBfTenPool = Number(draft.wdvBfPools!.tenPool) || 0;
            out.wdvBfTwentyPool = Number(draft.wdvBfPools!.twentyPool) || 0;
            out.wdvBfThirtyPool = Number(draft.wdvBfPools!.thirtyPool) || 0;
            out.wdvBfHpThirtyPool = Number(draft.wdvBfPools!.hpThirtyPool) || 0;
            out.baTenPool = Number(draft.baPools!.tenPool) || 0;
            out.baTwentyPool = Number(draft.baPools!.twentyPool) || 0;
            out.baThirtyPool = Number(draft.baPools!.thirtyPool) || 0;
            out.baHpThirtyPool = Number(draft.baPools!.hpThirtyPool) || 0;
            // salesProceedTen/TwentyPool: GOD-derived on backend assemble — do not round-trip from UI
            out.commercialFullyUsedPriorYears = Number(draft.commercialFullyUsedPriorYears) || 0;
            out.leaseholdFullyUsedPriorYears = Number(draft.leaseholdFullyUsedPriorYears) || 0;
        }
        return out;
    }, [annualId, companyId, effectiveYoa]);

    const syncLastSavedPayloadBaseline = useCallback(() => {
        lastSavedPayloadRef.current = buildTaxComPayloadKey(buildCurrentP1Payload());
    }, [buildCurrentP1Payload]);
    const syncBaselineRef = useRef(syncLastSavedPayloadBaseline);
    syncBaselineRef.current = syncLastSavedPayloadBaseline;

    /**
     * Stamp duty / Commission and legal fees / Reducing value b/f（含 leasehold WDV b/f）
     * 变更时按 P1.1 同一 4% 公式无感联动 P1 Commercial building + lessTotal。
     */
    const linkP1CommercialBuildingFromDraft = useCallback((payload?: ReturnType<typeof buildCurrentP1Payload>) => {
        const out = payload ?? buildCurrentP1Payload();
        const linkInput = {
            p11: getP11Data(),
            properties: out.properties,
            oneReducingBf: Number(out.oneReducingBf) || 0,
            wdvBf: Number(out.wdvBf) || 0,
            commercialFullyUsedPriorYears: out.commercialFullyUsedPriorYears,
            leaseholdFullyUsedPriorYears: out.leaseholdFullyUsedPriorYears,
            commercialPriorValue: out.commercialPriorValue,
            leaseholdPriorValue: out.leaseholdPriorValue,
        };
        const linkedCommercialBuilding = resolveP1CommercialBuildingFromSavedProperties(linkInput);
        if (linkedCommercialBuilding == null) {
            return;
        }

        const patchedP11 = patchP11CommercialAllowances(getP11Data(), linkInput);
        if (patchedP11 && patchedP11 !== getP11Data()) {
            applyP11Data(patchedP11);
        }

        const patchedMain = patchTaxMainCommercialBuilding(getTaxMainData(), linkedCommercialBuilding);
        if (patchedMain && patchedMain !== getTaxMainData()) {
            applyTaxComputationResult(patchedMain);
            setTaxMainData(patchedMain);
        }
    }, [applyP11Data, applyTaxComputationResult, buildCurrentP1Payload, getP11Data, getTaxMainData]);

    const lastSavedInputRef = useRef<{ taxPayment: number; taxLossBf: number }>({
        taxPayment: 0,
        taxLossBf: 0,
    });

    const [taxInfo, setTaxInfo] = useState<{
        companyName: string;
        yearOfAssessment: string | null;
        presentationCurrency: string | null;
        taxFileNo: string | null;
    } | null>(null);

    const [taxMainData, setTaxMainData] = useState<TaxComputationResult | null>(null);
    const [taxType, setTaxType] = useState<TaxComputationType | null>(null);

    const applyComputedTabStateFromStore = useCallback((mode: 'replaceExtras' | 'mergeExtras' = 'replaceExtras') => {
        const mainData = getTaxMainData();
        setTaxType(normalizeTaxComputationType(mainData?.type) || null);

        const nextAdd = (mainData?.extraAddItems || []).map((item, idx) => ({
            id: item.id,
            clientKey: item.id != null ? `id-${item.id}` : `add-${idx}`,
            section: 'ADD' as const,
            itemName: item.itemName ?? '',
            amount: Number(item.amount) || 0,
            sortNo: item.sortNo ?? idx,
        }));
        const nextLess = (mainData?.extraLessItems || []).map((item, idx) => ({
            id: item.id,
            clientKey: item.id != null ? `id-${item.id}` : `less-${idx}`,
            section: 'LESS' as const,
            itemName: item.itemName ?? '',
            amount: Number(item.amount) || 0,
            sortNo: item.sortNo ?? idx,
        }));
        const resolvedAdd = mode === 'mergeExtras'
            ? mergeHydratedExtraItems(extraAddItemsRef.current, nextAdd)
            : nextAdd;
        const resolvedLess = mode === 'mergeExtras'
            ? mergeHydratedExtraItems(extraLessItemsRef.current, nextLess)
            : nextLess;
        setExtraAddItems(resolvedAdd);
        setExtraLessItems(resolvedLess);
        extraAddItemsRef.current = resolvedAdd;
        extraLessItemsRef.current = resolvedLess;
        setTaxMainData(mainData
            ? {
                ...patchProfitsTaxFromLessTotal(
                    mainData,
                    recomputeLessTotalWithExtras(mainData, resolvedAdd, resolvedLess),
                ),
                extraAddItems: resolvedAdd,
                extraLessItems: resolvedLess,
            }
            : mainData);

        const nextP11 = getP11Data();
        setHasP11Data(!!nextP11);

        const nextP11a = getP11aData();
        setHasP11aData(!!nextP11a);
        setP11aData(nextP11a || null);

        const nextP12 = getP12Data();
        setHasP12Data(!!nextP12);
        setP12Data(nextP12 || null);

        const nextP121 = getP121Data();
        setHasP121Data(!!nextP121);
        setP121Data(nextP121 || null);

        const nextP13 = getP13Data();
        setHasP13Data(!!nextP13);
        setP13Data(nextP13 || null);
    }, [getTaxMainData, getP11Data, getP11aData, getP12Data, getP121Data, getP13Data]);

    const resetComputedTabState = useCallback(() => {
        setTaxInfo(null);
        setTaxMainData(null);
        setTaxType(null);
        setHasP11Data(false);
        setHasP11aData(false);
        setHasP12Data(false);
        setHasP121Data(false);
        setHasP13Data(false);
        setP11aData(null);
        setP12Data(null);
        setP121Data(null);
        setP13Data(null);
        setTaxPayment(0);
        setTaxLossBf(0);
        setWdvBfPools(null);
        setBaPools(null);
        setSalesProceedPools(null);
        setExtraAddItems([]);
        setExtraLessItems([]);
        extraAddItemsRef.current = [];
        extraLessItemsRef.current = [];
    }, []);

    const handleExtraAddChange = useCallback((items: TaxExtraItem[]) => {
        setExtraAddItems(items);
        extraAddItemsRef.current = items;
        setTaxMainData((prev) => {
            if (!prev) return prev;
            const lessTotal = recomputeLessTotalWithExtras(prev, items, extraLessItemsRef.current);
            return {
                ...patchProfitsTaxFromLessTotal(prev, lessTotal),
                extraAddItems: items,
            };
        });
    }, []);

    const handleExtraLessChange = useCallback((items: TaxExtraItem[]) => {
        setExtraLessItems(items);
        extraLessItemsRef.current = items;
        setTaxMainData((prev) => {
            if (!prev) return prev;
            const lessTotal = recomputeLessTotalWithExtras(prev, extraAddItemsRef.current, items);
            return {
                ...patchProfitsTaxFromLessTotal(prev, lessTotal),
                extraLessItems: items,
            };
        });
    }, []);

    const closeButtonRef = useRef<HTMLButtonElement | null>(null);

    /*
     * CLOSED: standalone Tax Com Excel merged into Zip (/fs-preview-download/download-zip).
     * Backend GET /tax-com/export-taxCom is commented closed.
     */
    // const handleExportTaxCom = async () => { ... };

    const tabKeys = useMemo(() => {
        const keys: string[] = ['p1'];
        if (hasP11Data) keys.push('p11');
        if (hasP11aData) keys.push('p11a');
        if (hasP12Data) keys.push('p12');
        if (hasP121Data) keys.push('p121');
        if (hasP13Data) keys.push('p13');
        keys.push('plAttach');
        return keys;
    }, [hasP11Data, hasP11aData, hasP12Data, hasP121Data, hasP13Data]);

    const loadingSkeleton = (
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column'}}>
            <Box sx={{borderBottom: 1, borderColor: 'divider', px: 2, py: 1}}>
                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                    {Array.from({length: 5}).map((_, index) => (
                        <Skeleton key={index} variant="rounded" width={48} height={32}/>
                    ))}
                </Box>
            </Box>

            <Box sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 0.5}}>
                {Array.from({length: 20}).map((_, index) => (
                    <Skeleton key={index} variant="rounded" height={32}/>
                ))}
            </Box>
        </Box>
    );

    const initializeData = async (options?: {forceRefresh?: boolean; silent?: boolean}) => {
        const silent = !!options?.silent;
        if (!silent) {
            setLoading(true);
            setTabValue(0);
            setVisitedTabKeys(new Set(['p1']));
        }
        try {
            const companyDetails = await getCompanyDetailsInfo(annualId);
            // Prefer prop from FsAdjustment; annual-cache-only lookup can miss companyId and blank the dialog
            const propCid = Number(companyIdProp) || 0;
            const detailCid = Number(companyDetails?.companyId) || 0;
            const compId = propCid > 0 ? propCid : detailCid;
            setCompanyId(compId);

            if (compId > 0) {
                // 后端 queryTaxCombined 已处理 principalActivities == 0 以及子附表空数据逻辑，
                // 前端不再需要提前判断 isInactive
                await initialize(compId, annualId, effectiveYoa || undefined);

                if (options?.forceRefresh) {
                    await refresh();
                }

                applyComputedTabStateFromStore();

                const mainData = getTaxMainData();
                const normalizedType = normalizeTaxComputationType(mainData?.type);
                const initPayment = normalizedType === TaxComputationType.TYPE_B
                    ? (Number(mainData?.profitsTaxB?.provisionalTaxPayment) || 0)
                    : normalizedType === TaxComputationType.TYPE_C
                        ? (Number(mainData?.profitsTaxC?.provisionalTaxPayment) || 0)
                        : 0;
                setTaxPayment(initPayment);

                const initTaxLossBf = normalizedType === TaxComputationType.TYPE_A
                    ? (Number(mainData?.profitsTaxA?.taxLossBf) || 0)
                    : normalizedType === TaxComputationType.TYPE_B
                        ? (Number(mainData?.profitsTaxB?.taxLossBf) || 0)
                        : normalizedType === TaxComputationType.TYPE_C
                            ? (Number(mainData?.profitsTaxC?.taxLossBf) || 0)
                            : 0;
                // Loss b/f must be <= 0. Positive API values are not losses (mis-seeded prior
                // assessable profit) — treat as 0. Do NOT negate: that persisted fake losses
                // (e.g. +84999 → -84999) and abandoned MULTI Tax Auto for the later YOA.
                const normalizedTaxLossBf = initTaxLossBf > 0 ? 0 : initTaxLossBf;
                setTaxLossBf(normalizedTaxLossBf);

                const {note: initP1Note, confirm: initP1Confirm} = getP1NoteConfirmFromResult(mainData);
                // null = 未编辑，展示走后端 fallback/default；勿写成 '' 否则会当成用户清空并写回 DB
                setP1Note(initP1Note?.trim() ? initP1Note.trim() : null);
                setP1Confirm(initP1Confirm?.trim() ? initP1Confirm.trim() : null);
                p1DraftRef.current = {
                    ...p1DraftRef.current,
                    p1Note: initP1Note?.trim() ? initP1Note.trim() : null,
                    p1Confirm: initP1Confirm?.trim() ? initP1Confirm.trim() : null,
                };

                lastSavedInputRef.current = {
                    taxPayment: initPayment,
                    taxLossBf: normalizedTaxLossBf,
                };

                const info = await getTaxComputationInfo(annualId);
                setTaxInfo({
                    ...info,
                    yearOfAssessment: effectiveYoa || info?.yearOfAssessment || null,
                });

                const p11Data = getP11Data();

                const nextOneReducingBf = Number(p11Data?.commercialBuilding?.reducingValueBf) || 0;
                const nextWdvBf = Number(p11Data?.thisYearCommercialBuilding?.wdvBf ?? p11Data?.thisYearCommercialBuilding?.reducingValueBf) || 0;
                setOneReducingBf(nextOneReducingBf);
                setWdvBf(nextWdvBf);
                hasHydratedP11BfRef.current = true;

                const zeroPools: BfPools = {tenPool: 0, twentyPool: 0, thirtyPool: 0, hpThirtyPool: 0};
                let nextWdvPools: BfPools | null = null;
                let nextBaPools: BfPools | null = null;
                let nextSalesPools: BfPools | null = null;

                if (p11Data) {
                    const wdvBfRow = (p11Data?.balancingList || []).find((r: any) => (r?.rowName || '').trim() === 'W.D.V. b/f') || null;
                    nextWdvPools = wdvBfRow
                        ? {
                            tenPool: Number(wdvBfRow?.tenPool) || 0,
                            twentyPool: Number(wdvBfRow?.twentyPool) || 0,
                            thirtyPool: Number(wdvBfRow?.thirtyPool) || 0,
                            hpThirtyPool: Number(wdvBfRow?.hpThirtyPool) || 0,
                        }
                        : {...zeroPools};

                    const baRow = (p11Data?.balancingList || []).find((r: any) => (r?.rowName || '').trim() === 'Balancing allowance') || null;
                    nextBaPools = baRow
                        ? {
                            tenPool: Number(baRow?.tenPool) || 0,
                            twentyPool: Number(baRow?.twentyPool) || 0,
                            thirtyPool: Number(baRow?.thirtyPool) || 0,
                            hpThirtyPool: Number(baRow?.hpThirtyPool) || 0,
                        }
                        : {...zeroPools};

                    const salesProceedRow = (p11Data?.balancingList || []).find((r: any) => (r?.rowName || '').trim() === 'Less: Sales proceed') || null;
                    nextSalesPools = salesProceedRow
                        ? {
                            tenPool: Number(salesProceedRow?.tenPool) || 0,
                            twentyPool: Number(salesProceedRow?.twentyPool) || 0,
                            thirtyPool: Number(salesProceedRow?.thirtyPool) || 0,
                            hpThirtyPool: Number(salesProceedRow?.hpThirtyPool) || 0,
                        }
                        : {...zeroPools};
                }

                // MULTI later YOA: W.D.V. b/f = prior YOA's computed W.D.V. c/f (session cache)
                if (isMultiYoa && !isFirstYoaTab) {
                    const yoaIdx = yoaTabs.indexOf((effectiveYoa || '').trim());
                    const priorYoa = yoaIdx > 0 ? yoaTabs[yoaIdx - 1] : '';
                    const priorCf = priorYoa ? wdvCfByYoaRef.current[priorYoa] : undefined;
                    if (priorCf) {
                        nextWdvPools = {
                            tenPool: Number(priorCf.tenPool) || 0,
                            twentyPool: Number(priorCf.twentyPool) || 0,
                            thirtyPool: Number(priorCf.thirtyPool) || 0,
                            hpThirtyPool: Number(priorCf.hpThirtyPool) || 0,
                        };
                    }
                }

                setWdvBfPools(nextWdvPools);
                setBaPools(nextBaPools);
                setSalesProceedPools(nextSalesPools);

                const nextCommercialFullyUsed = Number(p11Data?.commercialBuilding?.fullyUsedInPriorYears) || 0;
                const nextLeaseholdFullyUsed = Number(p11Data?.thisYearCommercialBuilding?.fullyUsedInPriorYears) || 0;
                setCommercialFullyUsedPriorYears(nextCommercialFullyUsed);
                setLeaseholdFullyUsedPriorYears(nextLeaseholdFullyUsed);

                // Sync draft immediately so baseline / early close do not read stale zero pools
                p1DraftRef.current = {
                    ...p1DraftRef.current,
                    oneReducingBf: nextOneReducingBf,
                    wdvBf: nextWdvBf,
                    wdvBfPools: nextWdvPools,
                    baPools: nextBaPools,
                    salesProceedPools: nextSalesPools,
                    commercialFullyUsedPriorYears: nextCommercialFullyUsed,
                    leaseholdFullyUsedPriorYears: nextLeaseholdFullyUsed,
                };

                const priorLabel = p11Data?.commercialBuilding?.commercialPriorLabel;
                const priorValue = p11Data?.commercialBuilding?.commercialPriorValue;
                const hydratedLabel = priorLabel != null && String(priorLabel).trim() !== ''
                    ? String(priorLabel).trim()
                    : null;
                const hydratedValue = priorValue != null && Number.isFinite(Number(priorValue))
                    ? Number(priorValue)
                    : null;
                // Both must be present to activate override mode; otherwise treat as cleared
                if (hydratedLabel != null && hydratedValue != null) {
                    setCommercialPriorLabel(hydratedLabel);
                    setCommercialPriorValue(hydratedValue);
                    p1DraftRef.current = {
                        ...p1DraftRef.current,
                        commercialPriorLabel: hydratedLabel,
                        commercialPriorValue: hydratedValue,
                    };
                } else {
                    setCommercialPriorLabel(null);
                    setCommercialPriorValue(null);
                    p1DraftRef.current = {
                        ...p1DraftRef.current,
                        commercialPriorLabel: null,
                        commercialPriorValue: null,
                    };
                }

                const lhLabelRaw = p11Data?.thisYearCommercialBuilding?.leaseholdPriorLabel;
                const lhValueRaw = p11Data?.thisYearCommercialBuilding?.leaseholdPriorValue;
                const lhLabel = lhLabelRaw != null && String(lhLabelRaw).trim() !== ''
                    ? String(lhLabelRaw).trim()
                    : null;
                const lhValue = lhValueRaw != null && Number.isFinite(Number(lhValueRaw))
                    ? Number(lhValueRaw)
                    : null;
                if (lhLabel != null && lhValue != null) {
                    setLeaseholdPriorLabel(lhLabel);
                    setLeaseholdPriorValue(lhValue);
                    p1DraftRef.current = {
                        ...p1DraftRef.current,
                        leaseholdPriorLabel: lhLabel,
                        leaseholdPriorValue: lhValue,
                    };
                } else {
                    setLeaseholdPriorLabel(null);
                    setLeaseholdPriorValue(null);
                    p1DraftRef.current = {
                        ...p1DraftRef.current,
                        leaseholdPriorLabel: null,
                        leaseholdPriorValue: null,
                    };
                }
            } else {
                resetComputedTabState();
            }
        } catch (error) {
            console.error('[TaxDetailsTab] initializeData failed', {annualId, companyIdProp, error});
            resetComputedTabState();
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    const doSaveAndRefreshP1 = useCallback(async (
        overrides?: { taxPayment?: number; taxLossBf?: number }
    ) => {
        if (savingRef.current) {
            pendingSaveRef.current = true;
            return;
        }
        savingRef.current = true;
        try {
            const effectiveTaxPayment = Number(overrides?.taxPayment ?? taxPayment) || 0;
            const effectiveTaxLossBfRaw = Number(overrides?.taxLossBf ?? taxLossBf) || 0;
            const effectiveTaxLossBf = effectiveTaxLossBfRaw > 0 ? -Math.abs(effectiveTaxLossBfRaw) : effectiveTaxLossBfRaw;

            const out = buildCurrentP1Payload();
            if (overrides?.taxPayment !== undefined) {
                out.taxPayment = effectiveTaxPayment;
            }
            if (overrides?.taxLossBf !== undefined) {
                out.taxLossBf = effectiveTaxLossBf;
            }

            // Never persist with companyId=0 (seen in audit.log as tax compliance insert + cache eviction company=0)
            if (!(Number(out.companyId) > 0) || !(Number(out.annualId) > 0)) {
                console.error('[TaxDetailsTab] skip saveOrUpdate: invalid companyId/annualId', out);
                return;
            }

            // payload 对比：与上次保存内容相同则跳过，打破 save→refresh→save 循环
            const payloadKey = buildTaxComPayloadKey(out);
            if (payloadKey === lastSavedPayloadRef.current) {
                return;
            }

            const ok = await auditTaxComService.saveOrUpdate(out);
            if (ok) {
                lastSavedPayloadRef.current = payloadKey;
                lastSavedPropertiesRef.current = normalizePropertyList(out.properties);
                suppressAutoSaveRef.current = true;
                skipChildParentSyncRef.current = true;
                // 无感刷新：queryTaxCombined 一次带回 P1 + P1.1/P1.2/P1.21/P1.3，不切 tab、不打 Skeleton
                await refresh();
                applyComputedTabStateFromStore('mergeExtras');

                // Stamp duty / CL fees / Reducing value b/f → 与 P1.1 4% 对齐后写回 P1
                linkP1CommercialBuildingFromDraft(out);

                const mainData = getTaxMainData();
                const {note: refreshedNote, confirm: refreshedConfirm} = getP1NoteConfirmFromResult(mainData);
                // Keep user-cleared empty text. Do not restore backend default "Note:...".
                if (p1DraftRef.current.p1Note == null && refreshedNote?.trim()) {
                    setP1Note(refreshedNote.trim());
                    p1DraftRef.current = {...p1DraftRef.current, p1Note: refreshedNote.trim()};
                }
                if (p1DraftRef.current.p1Confirm == null && refreshedConfirm?.trim()) {
                    setP1Confirm(refreshedConfirm.trim());
                    p1DraftRef.current = {...p1DraftRef.current, p1Confirm: refreshedConfirm.trim()};
                }
                lastSavedInputRef.current = {
                    taxPayment: effectiveTaxPayment,
                    taxLossBf: effectiveTaxLossBf,
                };
                // Tax Auto Adj 已更新：仅失效 adjustment list，沿用 FsAdjustment 既有 RQ 同步（无 Skeleton / 不清 store）
                const cid = Number(out.companyId || companyId || 0);
                const aid = Number(out.annualId || annualId || 0);
                if (cid && aid) {
                    await queryClient.invalidateQueries({
                        queryKey: ['adjustment', 'list', cid, aid],
                    });
                }
                // 等待 TaxSubP1 等子组件处理完 refresh 后的状态，再对齐 baseline 并解除抑制
                window.setTimeout(() => {
                    syncLastSavedPayloadBaseline();
                    lastSavedPropertiesRef.current = null;
                    skipChildParentSyncRef.current = false;
                    suppressAutoSaveRef.current = false;
                }, 1500);
            }
        } finally {
            savingRef.current = false;
            if (pendingSaveRef.current) {
                pendingSaveRef.current = false;
                const payloadKey = buildTaxComPayloadKey(buildCurrentP1Payload());
                if (payloadKey !== lastSavedPayloadRef.current) {
                    queueMicrotask(() => {
                        void doSaveRef.current?.();
                    });
                }
            }
        }
    }, [applyComputedTabStateFromStore, buildCurrentP1Payload, linkP1CommercialBuildingFromDraft, refresh, getTaxMainData, syncLastSavedPayloadBaseline, taxPayment, taxLossBf, companyId, annualId, queryClient]);

    // 始终保持 ref 指向最新的 doSaveAndRefreshP1
    doSaveRef.current = doSaveAndRefreshP1;

    // Stamp duty / Commission and legal fees / Reducing value b/f：编辑后立即无感联动 P1（不必等 save 返回）
    useEffect(() => {
        if (isFirstLoadRef.current || loading || !companyId) return;
        linkP1CommercialBuildingFromDraft();
    }, [
        p11Properties,
        oneReducingBf,
        wdvBf,
        commercialFullyUsedPriorYears,
        leaseholdFullyUsedPriorYears,
        commercialPriorValue,
        leaseholdPriorValue,
        loading,
        companyId,
        linkP1CommercialBuildingFromDraft,
    ]);

    const handleClose = useCallback(async () => {
        closeLastTaxDrawer();
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        const started = Date.now();
        while (savingRef.current && Date.now() - started < 8000) {
            pendingSaveRef.current = true;
            await new Promise((resolve) => setTimeout(resolve, 50));
        }
        await doSaveRef.current?.();
        onClose();
    }, [closeLastTaxDrawer, onClose]);

    // P1.1 字段变化时 debounce 自动保存（500ms）
    useEffect(() => {
        if (isFirstLoadRef.current || loading || !companyId) return;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            // suppress 仅拦截 refresh 引发的连锁 state 变更；用户编辑若与上次保存不同仍应落库
            if (suppressAutoSaveRef.current) {
                const payloadKey = buildTaxComPayloadKey(buildCurrentP1Payload());
                if (payloadKey === lastSavedPayloadRef.current) {
                    return;
                }
            }
            void doSaveRef.current?.();
        }, 500);
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [p11Properties, oneReducingBf, wdvBf, wdvBfPools, baPools, salesProceedPools, commercialFullyUsedPriorYears, leaseholdFullyUsedPriorYears, p1Note, p1Confirm, commercialPriorLabel, commercialPriorValue, leaseholdPriorLabel, leaseholdPriorValue, extraAddItems, extraLessItems, loading, companyId, buildCurrentP1Payload]);

    // Keep MULTI prior-YOA W.D.V. c/f cache fresh while pools / schedule rows change
    useEffect(() => {
        if (!open || loading || !isMultiYoa) return;
        cacheCurrentYoaWdvCf();
    }, [open, loading, isMultiYoa, wdvBfPools, baPools, salesProceedPools, hasP11Data, cacheCurrentYoaWdvCf]);

    useEffect(() => {
        if (!open) {
            clear();
            setTaxPayment(0);
            setTaxLossBf(0);
            setWdvBfPools(null);
            setBaPools(null);
            setSalesProceedPools(null);
            setCommercialFullyUsedPriorYears(0);
            setLeaseholdFullyUsedPriorYears(0);
            setP1Note(null);
            setP1Confirm(null);
            setCommercialPriorLabel(null);
            setCommercialPriorValue(null);
            setLeaseholdPriorLabel(null);
            setLeaseholdPriorValue(null);
            wdvCfByYoaRef.current = {};
            lastSavedInputRef.current = {taxPayment: 0, taxLossBf: 0};
            lastSavedPayloadRef.current = '';
            suppressAutoSaveRef.current = false;
            skipChildParentSyncRef.current = false;
            lastSavedPropertiesRef.current = null;
            pendingSaveRef.current = false;
            isFirstLoadRef.current = true;
            setSelectedYoa((initialYearOfAssessment || '').trim());
            return;
        }

        // Re-init (open / YOA / company) — block auto-save until hydrate + baseline settle
        isFirstLoadRef.current = true;
        hasHydratedP11BfRef.current = false;

        initializeData().then(() => {
            // 延迟开启自动保存：initializeData 完成后 TaxSubP1 等子组件的 effects
            // 还需要一个渲染周期来通过 callbacks 设置 parent state（wdvBfPools 等），
            // 如果立即 isFirstLoadRef = false，这些 state 变更会触发无效的 auto-save。
            // 延迟 > 500ms（debounce 间隔）确保 init 阶段的所有 state 稳定后再监听。
            if (initDelayTimerRef.current) clearTimeout(initDelayTimerRef.current);
            initDelayTimerRef.current = setTimeout(() => {
                // Align baseline to hydrated payload so view-only / close without edits skips saveOrUpdate
                syncBaselineRef.current();
                isFirstLoadRef.current = false;
                initDelayTimerRef.current = null;
            }, 600);
        });

        // 组件关闭/销毁时清理 store
        return () => {
            clear();
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            if (initDelayTimerRef.current) clearTimeout(initDelayTimerRef.current);
        };
        // effectiveYoa: MULTI YOA tab switch reloads queryTaxCombined for that slice
    }, [annualId, companyIdProp, clear, effectiveYoa, getCompanyDetailsInfo, getTaxComputationInfo, getTaxMainData, getP11Data, getP11aData, getP12Data, getP121Data, getP13Data, initialize, open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const currentKey = tabKeys[tabValue] || tabKeys[0];
        if (!currentKey) {
            return;
        }
        setVisitedTabKeys(prev => {
            if (prev.has(currentKey)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(currentKey);
            return next;
        });
    }, [open, tabKeys, tabValue]);

    // 当可用 tabs 变化时，确保当前 tabValue 不越界
    useEffect(() => {
        const count = 1
            + (hasP11Data ? 1 : 0)
            + (hasP11aData ? 1 : 0)
            + (hasP12Data ? 1 : 0)
            + (hasP121Data ? 1 : 0)
            + (hasP13Data ? 1 : 0)
            + 1;
        if (count > 0 && tabValue >= count) {
            setTabValue(0);
        }
    }, [hasP11Data, hasP11aData, hasP12Data, hasP121Data, hasP13Data, tabValue]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        const key = tabKeys[newValue];
        if (key) {
            setVisitedTabKeys(prev => {
                if (prev.has(key)) {
                    return prev;
                }
                const next = new Set(prev);
                next.add(key);
                return next;
            });
        }
    };

    let content: React.ReactNode;

    if (loading) {
        content = loadingSkeleton;
    } else {
        // 构建可用的 tabs
        const availableTabs = [];
        const tabPanels = [];

        let tabIndex = 0;

        // P1 Tab - main computation (always available when dialog opens)
        availableTabs.push(
            <Tab key="p1" label="P1" {...a11yProps(tabIndex)} />
        );
        tabPanels.push(
            <TabPanel key="p1-panel" value={tabValue} index={tabIndex} visited={visitedTabKeys.has('p1')}>
                <TaxMain
                    annualId={annualId}
                    taxInfo={taxInfo}
                    taxMainData={taxMainData}
                    taxType={taxType}
                    taxPayment={taxPayment}
                    taxPaymentChange={(n) => {
                        const next = Number(n) || 0;
                        setTaxPayment(next);
                        void doSaveAndRefreshP1({taxPayment: next});
                    }}
                    taxLossBf={taxLossBf}
                    taxLossBfChange={(n) => {
                        const next = n > 0 ? -Math.abs(n) : n;
                        setTaxLossBf(next);
                        void doSaveAndRefreshP1({taxLossBf: next});
                    }}
                    p1Note={p1Note}
                    p1Confirm={p1Confirm}
                    p1NoteChange={handleP1NoteChange}
                    p1ConfirmChange={handleP1ConfirmChange}
                    onP1TextBlur={flushDebouncedSave}
                    editableExtras
                    extraAddItems={extraAddItems}
                    extraLessItems={extraLessItems}
                    onExtraAddChange={handleExtraAddChange}
                    onExtraLessChange={handleExtraLessChange}
                />
            </TabPanel>
        );
        tabIndex += 1;

        // P1.1 Tab - 只在有数据时渲染
        if (hasP11Data) {
            availableTabs.push(
                <Tab key="p11" label="P1.1" {...a11yProps(tabIndex)} />
            );
            tabPanels.push(
                <TabPanel key="p11-panel" value={tabValue} index={tabIndex} visited={visitedTabKeys.has('p11')}>
                    <TaxSubP1
                        annualId={annualId}
                        companyId={companyId}
                        onPropertiesInit={handlePropertiesInit}
                        skipParentPoolSyncRef={skipChildParentSyncRef}
                        preferredPropertiesAfterSaveRef={lastSavedPropertiesRef}
                        oneReducingBf={oneReducingBf}
                        oneReducingBfChange={setOneReducingBf}
                        wdvBf={wdvBf}
                        wdvBfChange={setWdvBf}
                        wdvBfPools={wdvBfPools}
                        wdvBfPoolsChange={setWdvBfPools}
                        wdvBfEditable={wdvBfEditable}
                        baPools={baPools}
                        onBaPoolsChange={setBaPools}
                        salesProceedPools={salesProceedPools}
                        onSalesProceedPoolsChange={setSalesProceedPools}
                        commercialFullyUsedPriorYears={commercialFullyUsedPriorYears}
                        commercialFullyUsedPriorYearsChange={setCommercialFullyUsedPriorYears}
                        leaseholdFullyUsedPriorYears={leaseholdFullyUsedPriorYears}
                        leaseholdFullyUsedPriorYearsChange={setLeaseholdFullyUsedPriorYears}
                        commercialPriorLabel={commercialPriorLabel}
                        commercialPriorValue={commercialPriorValue}
                        onCommercialPriorOverrideChange={handleCommercialPriorOverrideChange}
                        leaseholdPriorLabel={leaseholdPriorLabel}
                        leaseholdPriorValue={leaseholdPriorValue}
                        onLeaseholdPriorOverrideChange={handleLeaseholdPriorOverrideChange}
                    />
                </TabPanel>
            );
            tabIndex += 1;
        }

        // P1.1a Tab — withholding tax (Excel P1.1 (2))
        if (hasP11aData) {
            availableTabs.push(
                <Tab key="p11a" label="P1.1a" {...a11yProps(tabIndex)} />
            );
            tabPanels.push(
                <TabPanel key="p11a-panel" value={tabValue} index={tabIndex} visited={visitedTabKeys.has('p11a')}>
                    <TaxSubP11a annualId={annualId} p11aData={p11aData}/>
                </TabPanel>
            );
            tabIndex += 1;
        }

        // P1.2 Tab - 只在有数据时渲染
        if (hasP12Data) {
            availableTabs.push(
                <Tab key="p12" label="P1.2" {...a11yProps(tabIndex)} />
            );
            tabPanels.push(
                <TabPanel key="p12-panel" value={tabValue} index={tabIndex} visited={visitedTabKeys.has('p12')}>
                    <TaxSubP2 annualId={annualId} p12Data={p12Data}/>
                </TabPanel>
            );
            tabIndex += 1;
        }

        // P1.21 Tab - 只在有数据时渲染
        if (hasP121Data) {
            availableTabs.push(
                <Tab key="p121" label="P1.21" {...a11yProps(tabIndex)} />
            );
            tabPanels.push(
                <TabPanel key="p121-panel" value={tabValue} index={tabIndex} visited={visitedTabKeys.has('p121')}>
                    <TaxSubP21 annualId={annualId} p121Data={p121Data}/>
                </TabPanel>
            );
            tabIndex += 1;
        }

        // P1.3 Tab - title 改为 P1.3（原 P3）
        if (hasP13Data) {
            availableTabs.push(
                <Tab key="p13" label="P1.3" {...a11yProps(tabIndex)} />
            );
            tabPanels.push(
                <TabPanel key="p13-panel" value={tabValue} index={tabIndex} visited={visitedTabKeys.has('p13')}>
                    <TaxSubP3 annualId={annualId} p13Data={p13Data}/>
                </TabPanel>
            );
            tabIndex += 1;
        }

        // PL Attach Tab - ProfitLoss 只读渲染（item + Audited Round）
        availableTabs.push(
            <Tab key="plAttach" label="PL" {...a11yProps(tabIndex)} />
        );
        tabPanels.push(
            <TabPanel key="plAttach-panel" value={tabValue} index={tabIndex} visited={visitedTabKeys.has('plAttach')}>
                <TaxPL annualId={annualId} yearOfAssessment={effectiveYoa || undefined}/>
            </TabPanel>
        );
        tabIndex += 1;

        const lastTaxAction = !isFirstAudit ? (
            <Button
                size="small"
                variant="outlined"
                startIcon={
                    uploadingLastTax
                        ? <CircularProgress size={14} />
                        : hasLastTax
                            ? <VisibilityOutlinedIcon fontSize="small" />
                            : <UploadOutlinedIcon fontSize="small" />
                }
                disabled={uploadingLastTax || !companyId || !annualId}
                onClick={handleLastTaxClick}
            >
                Last Tax
            </Button>
        ) : null;

        content = (
            <Box sx={{width: '100%', display: 'flex', flexDirection: 'column'}}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Tabs
                        value={tabValue}
                        onChange={handleChange}
                        aria-label="tax computation tabs"
                        sx={{flex: 1, minWidth: 0}}
                    >
                        {availableTabs}
                    </Tabs>
                    {lastTaxAction}
                </Box>
                <Box sx={{flex: 1, p: 2}}>
                    {tabPanels}
                </Box>
            </Box>
        );
    }

    const yoaTabsBar = isMultiYoa ? (
        <Tabs
            value={effectiveYoa}
            onChange={(_, nextYoa: string) => {
                if (!nextYoa || nextYoa === effectiveYoa) return;
                cacheCurrentYoaWdvCf();
                flushDebouncedSave();
                isFirstLoadRef.current = true;
                lastSavedPayloadRef.current = '';
                setSelectedYoa(nextYoa);
            }}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="tax year of assessment tabs"
            sx={{minHeight: 36, borderTop: 1, borderColor: 'divider', pt: 0.5}}
        >
            {yoaTabs.map((yoa) => (
                <Tab
                    key={yoa}
                    value={yoa}
                    label={yoa}
                    sx={{minHeight: 36, py: 0.5, textTransform: 'none'}}
                />
            ))}
        </Tabs>
    ) : null;

    const showHeaderToolbar = !hideStandaloneExport || !embedded;
    const toolbarActions = showHeaderToolbar ? (
        <Box sx={{ml: 'auto', display: 'flex', alignItems: 'center', gap: 1}}>
            {/* CLOSED: Tax Com Excel → Zip only (/fs-preview-download/download-zip) */}
            {!embedded && (
                <IconButton
                    ref={closeButtonRef}
                    autoFocus
                    aria-label="close"
                    onClick={() => { void handleClose(); }}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon/>
                </IconButton>
            )}
        </Box>
    ) : null;

    const body = (
        <>
            {content}
            <LastTaxDrawer
                open={lastTaxDrawerOpen}
                width={lastTaxDrawerWidth}
                fileName={lastTaxMeta?.fileName}
                previewUrl={lastTaxPreviewUrl}
                uploading={uploadingLastTax}
                onClose={closeLastTaxDrawer}
                onReplace={handleReplaceClick}
                onWidthChange={setLastTaxMeasuredWidth}
            />
            <Snackbar
                open={!!snack?.open}
                autoHideDuration={3000}
                onClose={() => setSnack(s => (s ? {...s, open: false} : s))}
                message={snack?.message}
            />
        </>
    );

    return (
        <>
        <input
            ref={lastTaxFileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            hidden
            onChange={handleLastTaxFileSelected}
        />
        {embedded ? (
            <Box sx={{display: open ? 'block' : 'none'}}>
                {(toolbarActions || yoaTabsBar) && (
                    <Box sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                        {toolbarActions && (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                {toolbarActions}
                            </Box>
                        )}
                        {yoaTabsBar}
                    </Box>
                )}
                {body}
            </Box>
        ) : (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                // Drawer is hideBackdrop sibling Modal; outside click can look like Dialog backdropClick
                if (lastTaxDrawerOpen && reason === 'backdropClick') {
                    return;
                }
                void handleClose();
            }}
            maxWidth="lg"
            fullWidth
            // Align with BankLoanDetail: both Dialog and Drawer stay focusable / editable
            disableEnforceFocus
            disableRestoreFocus={lastTaxDrawerOpen}
            closeAfterTransition={false}
            sx={{
                zIndex: (theme) => theme.zIndex.modal,
                transition: 'transform 300ms ease',
                transform: `translateX(${lastTaxShiftX}px)`,
            }}
            PaperProps={{
                ref: taxDialogPaperRef,
                sx: {
                    maxWidth: 1320,
                },
            }}
            TransitionProps={{
                onEntered: () => {
                    closeButtonRef.current?.focus();
                },
            }}
        >
            <DialogTitle sx={{m: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                {toolbarActions && (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {toolbarActions}
                    </Box>
                )}
                {yoaTabsBar}
            </DialogTitle>
            <DialogContent dividers sx={{p: 0}}>
                {body}
            </DialogContent>
        </Dialog>
        )}
        </>
    );
}
